import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';
import type { PagedData } from './common';

export interface CoAction {
  id: string;
  projectId: string;
  title: string;
  content: string;
  contentTokenCount?: number | null;
  status: string;
  sessionStatus: CoActionSessionStatus | null;
  visibility: string;
  source: 'MANUAL' | 'AUTO_SESSION';
  conversationKey?: string | null;
  daemonTriggerId?: string | null;
  createdByMemberId?: string | null;
  createdByAgentId?: string | null;
  createdByType?: string | null;
  /** 경유 축. 도구 축을 특정하지 못해도 웹 작성물과 에이전트 표면 작성물을 구분한다. */
  createdByCredentialKind?: string | null;
  createdByName?: string | null;
  createdByAgentName?: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  planCount: number;
  completionReportCount: number;
  postMortemCount: number;
  takeawayCount: number;
  linkedPlans?: { id: string; title: string; status: string; priority: string }[];
  linkedCompletionReports?: { id: string; title: string; status: string; qualityScore: number | null }[];
  linkedPostMortems?: { id: string; title: string; status: string }[];
  linkedTakeaways?: CoActionTakeaway[];
  linkedDaemonTrigger?: { id: string; status: string; runnerType: string } | null;
  webUrl?: string | null;
}

export type CoActionSessionStatus = 'RUNNING' | 'NEEDS_REVIEW' | 'COMPLETED' | 'FAILED' | 'CANCELLED' | 'UNKNOWN';

export type CoActionListItem = Omit<CoAction, 'content'>;

type CoActionListResponse = {
  data: CoActionListItem[];
};

type CoActionPagedResponse = {
  data: CoActionListItem[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
};

type CoActionResponse = {
  data: CoAction;
};

export const fetchCoActions = async (
  token: string,
  projectId: string,
  params?: {
    status?: string;
    visibility?: string;
    source?: string;
    createdByMemberId?: string;
  },
): Promise<CoActionListItem[]> => {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.set('status', params.status);
  if (params?.visibility) queryParams.set('visibility', params.visibility);
  if (params?.source) queryParams.set('source', params.source);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);

  const url = `${getApiUrl()}/api/projects/${projectId}/co-actions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-actions: ${response.status}`);
  }

  const data = (await response.json()) as CoActionListResponse;
  return data.data;
};

export const fetchCoActionsPaged = async (
  token: string,
  projectId: string,
  params?: {
    search?: string;
    status?: string;
    visibility?: string;
    source?: string;
    createdByMemberId?: string;
    dateFrom?: string;
    dateTo?: string;
    page?: number;
    pageSize?: number;
  },
): Promise<PagedData<CoActionListItem>> => {
  const queryParams = new URLSearchParams();
  if (params?.search) queryParams.set('search', params.search);
  if (params?.status) queryParams.set('status', params.status);
  if (params?.visibility) queryParams.set('visibility', params.visibility);
  if (params?.source) queryParams.set('source', params.source);
  if (params?.createdByMemberId) queryParams.set('createdByMemberId', params.createdByMemberId);
  if (params?.dateFrom) queryParams.set('dateFrom', params.dateFrom);
  if (params?.dateTo) queryParams.set('dateTo', params.dateTo);
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/co-actions${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-actions: ${response.status}`);
  }

  const data = (await response.json()) as CoActionPagedResponse;
  return data;
};

export const fetchCoActionById = async (token: string, projectId: string, id: string): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/co-actions/${id}`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-action: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

/**
 * 코액션 상세 read-only GraphQL client (Mercurius).
 *
 * REST `fetchCoActionById`와 같은 CoAction 모델로 다시 조립해 상세 View는 transport를 알 필요가 없다.
 * GraphQL 오류는 호출자에서 REST 폴백할 수 있도록 그대로 throw한다.
 */
const CO_ACTION_DETAIL_QUERY = `
  query CoActionDetail($projectId: ID!, $id: ID!) {
    coActionDetail(projectId: $projectId, id: $id) {
      coAction
      linkedPlans
      linkedCompletionReports
      linkedPostMortems
      linkedTakeaways
      linkedDaemonTrigger
    }
  }
`;

type CoActionDetailGraphqlData = {
  coAction: Omit<
    CoAction,
    'linkedPlans' | 'linkedCompletionReports' | 'linkedPostMortems' | 'linkedTakeaways' | 'linkedDaemonTrigger'
  >;
  linkedPlans: NonNullable<CoAction['linkedPlans']>;
  linkedCompletionReports: NonNullable<CoAction['linkedCompletionReports']>;
  linkedPostMortems: NonNullable<CoAction['linkedPostMortems']>;
  linkedTakeaways: NonNullable<CoAction['linkedTakeaways']>;
  linkedDaemonTrigger: CoAction['linkedDaemonTrigger'];
};

type CoActionDetailGraphqlPayload = {
  data?: { coActionDetail: CoActionDetailGraphqlData | null } | null;
  errors?: Array<{ message: string }>;
};

export const fetchCoActionDetailGraphql = async (token: string, projectId: string, id: string): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/graphql`, {
    method: 'POST',
    cache: 'no-store',
    headers: authHeaders(token, true),
    body: JSON.stringify({
      query: CO_ACTION_DETAIL_QUERY,
      variables: { projectId, id },
      operationName: 'CoActionDetail',
    }),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-action detail: ${response.status}`);
  }

  const payload = (await response.json()) as CoActionDetailGraphqlPayload;
  if (payload.errors && payload.errors.length > 0) {
    throw new Error(payload.errors[0]?.message ?? 'GraphQL co-action detail failed');
  }
  const detail = payload.data?.coActionDetail;
  if (!detail) {
    throw new Error('GraphQL co-action detail returned no data');
  }

  return {
    ...detail.coAction,
    linkedPlans: detail.linkedPlans,
    linkedCompletionReports: detail.linkedCompletionReports,
    linkedPostMortems: detail.linkedPostMortems,
    linkedTakeaways: detail.linkedTakeaways,
    linkedDaemonTrigger: detail.linkedDaemonTrigger,
  };
};

/** GraphQL을 기본 transport로 사용하고, 오류 시 기존 REST 상세 계약으로 안전하게 폴백한다. */
export const fetchCoActionDetail = async (token: string, projectId: string, id: string): Promise<CoAction> => {
  try {
    return await fetchCoActionDetailGraphql(token, projectId, id);
  } catch {
    return fetchCoActionById(token, projectId, id);
  }
};

export const updateCoAction = async (
  token: string,
  projectId: string,
  id: string,
  input: Partial<Pick<CoAction, 'title' | 'status' | 'visibility'>>,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/co-actions/${id}`, {
    method: 'PUT',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to update co-action: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export const deleteCoAction = async (token: string, projectId: string, id: string): Promise<void> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/co-actions/${id}`, {
    method: 'DELETE',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete co-action: ${response.status}`);
  }
};

export const linkPlanToCoAction = async (
  token: string,
  projectId: string,
  coActionId: string,
  planId: string,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/plans/${planId}`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to link plan: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export const unlinkPlanFromCoAction = async (
  token: string,
  projectId: string,
  coActionId: string,
  planId: string,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/plans/${planId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unlink plan: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export const linkCompletionReportToCoAction = async (
  token: string,
  projectId: string,
  coActionId: string,
  completionReportId: string,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/completion-reports/${completionReportId}`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to link completion report: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export const unlinkCompletionReportFromCoAction = async (
  token: string,
  projectId: string,
  coActionId: string,
  completionReportId: string,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/completion-reports/${completionReportId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unlink completion report: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export const linkPostMortemToCoAction = async (
  token: string,
  projectId: string,
  coActionId: string,
  postMortemId: string,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/post-mortems/${postMortemId}`,
    {
      method: 'POST',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to link post-mortem: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export const unlinkPostMortemFromCoAction = async (
  token: string,
  projectId: string,
  coActionId: string,
  postMortemId: string,
): Promise<CoAction> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/post-mortems/${postMortemId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to unlink post-mortem: ${response.status}`);
  }

  const data = (await response.json()) as CoActionResponse;
  return data.data;
};

export interface CoActionTakeaway {
  id: string;
  coActionId: string;
  content: string;
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  createdByName: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface CoActionHistory {
  id: string;
  coActionId: string;
  title: string;
  content: string;
  createdByMemberId: string | null;
  createdByAgentId: string | null;
  createdByType: string | null;
  createdByName: string | null;
  trigger: string | null;
  description: string | null;
  createdAt: string;
}

type CoActionTakeawayResponse = {
  data: CoActionTakeaway;
};

type CoActionTakeawayListResponse = {
  data: CoActionTakeaway[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
};

type CoActionHistoryListResponse = {
  data: CoActionHistory[];
  meta: { total: number; page: number; pageSize: number; totalPages: number };
};

export const fetchCoActionTakeaways = async (
  token: string,
  projectId: string,
  coActionId: string,
  params?: { page?: number; pageSize?: number },
): Promise<PagedData<CoActionTakeaway>> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/takeaways${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-action takeaways: ${response.status}`);
  }

  const data = (await response.json()) as CoActionTakeawayListResponse;
  return data;
};

export const createCoActionTakeaway = async (
  token: string,
  projectId: string,
  coActionId: string,
  body: { content: string },
): Promise<CoActionTakeaway> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/takeaways`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to create co-action takeaway: ${response.status}`);
  }

  const data = (await response.json()) as CoActionTakeawayResponse;
  return data.data;
};

export const deleteCoActionTakeaway = async (
  token: string,
  projectId: string,
  coActionId: string,
  goalId: string,
): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/takeaways/${goalId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to delete co-action takeaway: ${response.status}`);
  }
};

export interface CoActionTrigger {
  id: string;
  prompt: unknown;
  status: string;
  runnerType: string;
  model: string | null;
  startedAt: string | null;
  createdAt: string;
  agentConfigName: string;
}

type CoActionTriggerListResponse = {
  data: CoActionTrigger[];
};

export const fetchCoActionTriggers = async (
  token: string,
  projectId: string,
  coActionId: string,
): Promise<CoActionTrigger[]> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/triggers`,
    { cache: 'no-store', headers: authHeaders(token) },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-action triggers: ${response.status}`);
  }

  const data = (await response.json()) as CoActionTriggerListResponse;
  return data.data;
};

export const fetchCoActionHistories = async (
  token: string,
  projectId: string,
  coActionId: string,
  params?: { page?: number; pageSize?: number },
): Promise<PagedData<CoActionHistory>> => {
  const queryParams = new URLSearchParams();
  if (params?.page) queryParams.set('page', String(params.page));
  if (params?.pageSize) queryParams.set('pageSize', String(params.pageSize));

  const url = `${getApiUrl()}/api/projects/${projectId}/co-actions/${coActionId}/histories${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  const response = await fetchWithAuthRetry(url, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch co-action histories: ${response.status}`);
  }

  const data = (await response.json()) as CoActionHistoryListResponse;
  return data;
};
